import { useId, type ChangeEvent, type InputHTMLAttributes, type ReactNode, type Ref } from 'react';
import { cx } from '../internal/cx';
import { assignRef } from '../internal/refs';
import { useFieldProps } from './fieldContext';
import styles from './Form.module.css';

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> {
  /** Visible label. Without it, pass aria-label or aria-labelledby. */
  label?: ReactNode;
  description?: ReactNode;
  /** Mixed state (e.g. "select all" with some rows selected). */
  indeterminate?: boolean;
  invalid?: boolean;
  onCheckedChange?: (checked: boolean, event: ChangeEvent<HTMLInputElement>) => void;
  ref?: Ref<HTMLInputElement>;
}

export function Checkbox({
  label,
  description,
  indeterminate = false,
  invalid,
  className,
  id,
  required,
  disabled,
  onChange,
  onCheckedChange,
  'aria-describedby': ariaDescribedBy,
  ref,
  ...rest
}: CheckboxProps) {
  const descriptionId = useId();
  const field = useFieldProps({
    id,
    'aria-describedby': cx(description ? descriptionId : undefined, ariaDescribedBy) || undefined,
    invalid,
    required,
    disabled,
  });

  const input = (
    <span className={styles.checkWrap}>
      <input
        ref={(el) => {
          if (el) el.indeterminate = indeterminate;
          assignRef(ref, el);
        }}
        type="checkbox"
        id={field.id}
        className={styles.checkInput}
        aria-describedby={field.describedBy}
        aria-invalid={field.invalid || undefined}
        required={field.required || undefined}
        disabled={field.disabled || undefined}
        onChange={(event) => {
          onChange?.(event);
          onCheckedChange?.(event.target.checked, event);
        }}
        {...rest}
      />
      <span className={cx(styles.checkBox, indeterminate && styles.checkMixed)} aria-hidden="true">
        <svg viewBox="0 0 16 16" className={styles.checkMark} fill="none">
          {indeterminate ? (
            <path d="M4 8h8" strokeWidth="2" strokeLinecap="round" />
          ) : (
            <path
              d="M3.5 8.5l3 3 6-7"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
      </span>
    </span>
  );

  if (label == null) {
    return <span className={cx(styles.choice, styles.choiceBare, className)}>{input}</span>;
  }

  return (
    <div className={cx(styles.choice, field.disabled && styles.choiceDisabled, className)}>
      {input}
      <span className={styles.choiceText}>
        <label htmlFor={field.id} className={styles.choiceLabel}>
          {label}
        </label>
        {description && (
          <span id={descriptionId} className={styles.choiceDescription}>
            {description}
          </span>
        )}
      </span>
    </div>
  );
}
